/**
 * In-app documentation shipped in the default content bundle (spec section 12: versioned real
 * documentation with integration setup, workflows, field mappings, shortcuts, storage and retention,
 * error help, release notes and copyable support references).
 */
import { SYNC_ERROR_CODES, type SyncErrorCode } from '../status.js';
import type { HelpContentBody } from '../domain/templates.js';

/** A help article: the bundle body (domain/templates HelpContentBody) plus fixture metadata. */
export interface HelpArticle extends HelpContentBody {
  /** One-line summary shown in the documentation index. */
  summary: string;
  /** Copyable support reference for the article. */
  reference: string;
}

export type HelpSection = HelpContentBody['section'];

function article(input: Omit<HelpArticle, 'reference'>): HelpArticle {
  return { ...input, reference: `agency://docs/${input.slug}` };
}

/** Body of the help_content bundle item for an article (fixture metadata stripped). */
export function helpArticleBody(a: HelpArticle): HelpContentBody {
  return { slug: a.slug, title: a.title, section: a.section, markdown: a.markdown, order: a.order };
}

/** Template id of the help_content item for an article. */
export function helpTemplateId(slug: string): string {
  return `help.${slug}`;
}

export const HELP_ARTICLES: readonly HelpArticle[] = [
  article({
    slug: 'getting-started',
    title: 'Getting started',
    section: 'setup',
    order: 0,
    summary: 'First sign-in, device registration, the content bundle and selecting an account.',
    markdown: `# Getting started

Agency is an account-oriented workbench. Customer GRC data is downloaded to this Mac, stored in an encrypted local database and never mirrored to the Agency cloud. The Agency server holds only your identity, account assignments, permissions, shared templates and operational metadata.

## Before you begin

An Agency administrator must invite you and assign at least one customer account. Invitations are bound to your workforce identity; signing in with a matching email domain alone does not grant access. If sign-in reports that your identity is not invited, contact an administrator rather than retrying.

## First sign-in

1. Choose **Sign in**. Agency opens your system browser for the workforce identity provider (Authorization Code with PKCE). Agency never shows an embedded login page.
2. After the browser confirms, Agency registers this Mac as a device. The device holds a signing key used for audit batches and credential delivery; you do not need to manage it.
3. Agency requests a one-hour entitlement. The entitlement lists your capabilities and assigned accounts and is renewed automatically about ten minutes before it expires while you are online.
4. The current content bundle (templates, classification rules, help text and configuration) downloads and is verified against the Agency signing key.

## Selecting an account

The sidebar shows one account at a time. The account switcher at its top names the current account and opens a searchable list of every local account, with your **Priority** list and today's weekday list first; below it are that account's views: Overview, Sources, Agendas, Vulnerabilities, Infrastructure, Employee Compliance, Outreach, Rumi and Sync History. **Templates and Master Tests**, **Notepad**, **Documentation**, **Notifications** and **Settings** stay at the bottom of the sidebar. The active account and provider stay visible in the context bar above every view.

A newly selected account starts empty on this Mac. Connect its provider, review the discovered tenant and capabilities, then start the first sync yourself. Nothing is downloaded until you do. A second Mac starts empty as well; local history is never transferred between devices or analysts.

## Locking and renewal

If entitlement renewal fails, Agency shows the remaining access window. When the lease expires, local edits are autosaved, running provider work is cancelled at a safe checkpoint and customer-data views lock until you sign in again. Access revoked on the server takes effect on the next renewal at the latest, within one hour.`,
  }),
  article({
    slug: 'connecting-vanta',
    title: 'Connecting Vanta',
    section: 'setup',
    order: 1,
    summary: 'Client credentials, tenant discovery, the capability manifest and the browser URL.',
    markdown: `# Connecting Vanta

Agency reads Vanta through the Manage API using a read-only client credential that the customer authorises for you. Credentials are stored in the macOS credential store on this Mac only.

## What you need

- A Vanta OAuth client id and client secret with the read scope for the Manage API. The customer may need to create the application credential; Agency cannot assume every operator can.
- The tenant's browser URL (for example \`https://app.vanta.com/your-workspace\`). Agency stores it separately from the API base URL and uses it only for validated HTTPS deep links.

## Connect and Test

Open the account, choose **Connect** and enter the credentials. **Connect and Test** mints a token, discovers the tenant identity and probes each dataset in the manifest: tests, test entities, documents and policies, frameworks and controls, vulnerabilities, people, monitored computers, integrations and vendors. The result is a capability manifest that lists each dataset as supported or unsupported with the reason. An endpoint permission failure is reported as unsupported, never as an empty success.

If the discovered tenant does not match the tenant previously attached to this account, Agency refuses to attach the data and asks you to select or create the correct account. Replacing a credential keeps the account id and all local state.

## Rate limits and tokens

Vanta documents 50 Manage API requests per minute and 5 OAuth token requests per minute, and allows one active token per application. Agency operates at a 50 requests per minute ceiling by default, paces requests evenly and serialises token renewal. If several analysts share one application credential, Agency acquires a short coordination lease from the Agency server before minting a token so two Macs do not invalidate each other. Third-party scripts using the same credential can still invalidate the token; mention this during onboarding.

## Token diagnostics

Settings shows token expiry, scopes and connection health without minting a new token. Revealing or copying a token requires the credential.debug capability and a fresh local authentication, and records an audit event that the reveal occurred. The token value itself never enters audit events or support bundles.`,
  }),
  article({
    slug: 'connecting-drata',
    title: 'Connecting Drata',
    section: 'setup',
    order: 2,
    summary: 'API key, workspace discovery and the picker, verified dataset coverage.',
    markdown: `# Connecting Drata

Agency reads Drata through the public API with a bearer API key issued to you by the customer. Controls are the primary rows; monitors, monitor failures and evidence appear as related children.

## Workspace discovery

After you enter the API key, Agency lists the workspaces the key can reach. When there is more than one, an explicit picker appears and you must choose one before any sync. A connection identifies exactly one Drata workspace; connect again to add another workspace to the same account. Agency never merges workspaces or providers implicitly.

## Verified coverage

Connect and Test probes workspaces, controls, framework associations, monitors, monitor failures, evidence library metadata and personnel. Each dataset is marked supported or unsupported from actual endpoint results. If the personnel endpoint answers with a permission error, Employee Compliance and Outreach show **Unsupported** for this connection rather than an empty report.

## Readiness and status

Drata readiness (isReady) maps to a ready or not ready control. Readiness, monitoring status, evidence status, scope and your own overrides are kept as distinct fields. A ready control does not imply that every attached monitor passes. Missing booleans are shown as Unknown; they are never treated as passing.

## Rate limits and shared egress

Drata documents 500 requests per minute per source IP. Analysts in the same office or on the same VPN share that quota. Agency starts at a conservative ceiling (200 per minute by default) and coordinates an egress-group budget through the Agency server. If coordination is unavailable, new collection that needs the shared budget pauses until it returns.`,
  }),
  article({
    slug: 'connecting-google',
    title: 'Connecting Google',
    section: 'setup',
    order: 3,
    summary: 'OAuth in the system browser, minimal scopes, reconnecting and switching identities.',
    markdown: `# Connecting Google

Google is connected only when you publish an agenda or sheet or prepare outreach. Agency uses native-app OAuth with PKCE in your system browser and a short-lived loopback listener on this Mac. No embedded browser is used.

## Scopes

Agency requests the narrowest scopes that work: file-scoped Drive access for documents and sheets it creates or you explicitly select, and Gmail compose for drafts and permissioned sending. Gmail compose covers both drafting and sending; the Agency permission model, not the Google token, decides whether you can send. Your Google Workspace administrator may need to approve the application before the first connection.

## Sender identity

The Gmail profile and any approved send-as identity appear in every outreach preview. Switching Google identities does not change the customer account; artifact links remain attached to the account.

## Reconnect

If a scope changes with an app update or the refresh token is revoked, the affected actions show **Reconnect**. Notes, snapshots, overrides and artifact links are kept. Disconnecting Google removes the local token; it does not delete any Google file.`,
  }),
  article({
    slug: 'manual-sync-and-jobs',
    title: 'Manual sync and jobs',
    section: 'workflows',
    order: 10,
    summary: 'Sync Now, the job lifecycle, checkpoints, retry and resume, partial runs.',
    markdown: `# Manual sync and jobs

All provider collection is manual. Only **Connect and Test**, **Sync Now**, **Retry Failed Parts** and **Resume** read from Vanta or Drata. Opening a page, generating a report, asking Rumi, launching the app or receiving a template update never triggers collection.

## Starting a sync

Sync Now shows the proposed scope before any request is made: the connection, the datasets that will be collected, the estimated request count and the time that implies at the current rate ceiling. At 50 requests per minute, 2,000 requests take about 40 minutes before retries. Confirm to queue the job.

## Job states

A job moves through queued, preflight, fetching, validating, committing and succeeded. It may also be waiting for rate limit, paused, interrupted, partial, failed or cancelled. Every transition and the action that initiated it are recorded. Only one active job may collect a given connection at a time.

- **Preflight** checks entitlement, connection identity, credentials, scopes, adapter version, free disk, the requested datasets and any shared rate-limit lease.
- **Fetching** walks every cursor and child relationship, saving an encrypted page and a checkpoint after each durable page. Repeated cursors, duplicate ids, malformed pages and missing continuation fields are detected and reported.
- **Validating** reconciles provider totals where available and compares anomalous count changes against the last complete snapshot.
- **Committing** publishes the new snapshot atomically. A crash before commit leaves the previous snapshot pointer intact.

## Rate limits and retries

On a 429 Agency honours Retry-After, including the HTTP-date form, and otherwise backs off exponentially with jitter. The status line reads, for example, "Waiting for Vanta · retry in 18s". A request is retried up to five times within a five-minute budget; after that the job pauses and offers Resume. Cancel stops queued requests and lets the current safe checkpoint finish.

## Partial runs and resume

When some datasets fail, the run is kept as a distinct partial run for inspection and is never labelled the latest complete snapshot. Reports default to the last complete snapshot; choosing a partial run adds a visible coverage disclosure. Outreach cannot proceed while personnel, recipient mapping or the required compliance evidence is incomplete. Retry Failed Parts continues only the units whose scope and traversal are still valid; expired cursors restart the affected dataset while preserving the original attempt.

A paused, crashed, expired-entitlement or quit job requires your Resume action. Network restoration alone never starts a new job.

## Investigating issues

Each issue shows the run id, provider, connection, dataset, endpoint template, method, page ordinal, HTTP status, provider request id, stable error code, retry attempt, duration, timestamp, next retry and a suggested remedy. Raw responses stay encrypted on this Mac. See the error reference for every code.`,
  }),
  article({
    slug: 'source-explorer',
    title: 'Source explorer',
    section: 'workflows',
    order: 11,
    summary: 'Search, views, filters, parent and child navigation, overrides and JSON export.',
    markdown: `# Source explorer

Sources is the primary view for an account: a dense table of synced records with an inspector for the selected row. Every surface shows the snapshot time and completeness state; no label such as "Current" or "Synced" hides an incomplete dataset.

## Search and views

Search matches title, description, source id, remediation ask and framework. Views cover all sources, tests or controls, documents, included records, policies, infrastructure, company compliance and employee compliance. Show or hide OK, deactivated and irrelevant records independently. Provider framework filters and, for Drata, in-scope filtering narrow the table further.

## Parents and children

Vanta tests own test entities; Drata controls own monitors, monitor failures and evidence. When a filter matches a child, the parent row is kept with a matched-child count so context is not lost. The inspector shows raw provider status next to the normalised status, timestamps, children, links and the effective ask with its provenance.

## Overrides

Inclusion, irrelevance, classification flags and remediation text are analyst overrides and are stored separately from synced facts. A sync never resets them. Batch marking of visible empty asks as needing a custom ask feeds the missing-ask review queue, which shows progress counts.

A record that is absent from an incomplete response is not deleted. A record that disappears from a verified complete dataset is marked removed; its history and overrides follow the retention policy.

## Export

Export writes the currently filtered records as JSON with a manifest containing the account, snapshot, filters and schema version. Credentials and unrelated accounts are never included.`,
  }),
  article({
    slug: 'master-tests',
    title: 'Shared master tests and classifications',
    section: 'workflows',
    order: 12,
    summary: 'Canonical keys, classification flags, the ask resolution order and publishing changes.',
    markdown: `# Shared master tests and classifications

The master catalogue holds generic provider definitions and reusable asks keyed by a provider-qualified canonical key: provider, object type and the provider's catalogue identifier, for example \`vanta:test:screen-lock-enabled\` or \`drata:control:DCF-12\`. Tenant-specific ids stay scoped to the account and workspace.

## Flags

Each rule carries isPolicy, isInfrastructure, isEmployeeCompliance, isCompanyCompliance, a custom-ask-needed marker, a display description and editable remediation text. The Templates and Master Tests view shows which local accounts use each rule.

## Resolution order

The effective ask for a record is resolved in this order: local account override, local personal library override, published Agency rule, provider remediation, then a visible missing-ask state. The inspector names the layer that supplied the text.

## Private library and publishing

Operators may edit a private library that applies across their own accounts. Publishing a shared change is a separate permissioned action performed in the admin console with a diff and a preview of affected rules. Customer data is never promoted into the shared catalogue automatically, and unverified cross-tenant equivalence requires an explicit reviewed mapping.`,
  }),
  article({
    slug: 'agendas',
    title: 'Agendas',
    section: 'workflows',
    order: 13,
    summary: 'Sections, inclusion rules, placeholders, markers and safe Google Docs updates.',
    markdown: `# Agendas

A rolling agenda is a Google Doc that Agency updates before each customer meeting. An account can have several agendas for different meeting series while keeping one default.

## Layout

The document follows the Agency rolling agenda template: a bold header block ("{Customer} & Agency", "Bi-Weekly Status Call Rolling Meeting Agenda"), a rule, the meeting line "Monday, August 10th, 2026 | Asynchronous Update", and bold emoji headings: Advisories, Agenda, Current Blockers, {Customer} Asks, Status Per Framework, Relevant Links and Completed in Last Sprint. The Asks and Status Per Framework sections are managed by Agency; every other section is yours, starts as "- N/A" and is preserved verbatim on every update. Nothing in the document is written by a language model: every managed line comes from the snapshot and the published ask text.

The document has tabs. The Agenda tab holds the meeting; the Previously completed sprints tab holds the sprint history; when the Overall group exceeds its cap (15 by default, adjustable per agenda) the rest moves to an Additional asks tab and the Agenda tab says so. Documents created before tabs existed are upgraded with Repair → Add tabs.

## Asks

Each ask is one bullet in the customer's words ("Please enable screenlock on the following 2 device(s):"), with every affected child as a nested bullet; monitored computers read "MacBook Pro (Owned by name@customer.com)". Group labels (Overall, Infrastructure, Company Compliance, Employee Compliance) are italic lines; the line "Please work through …" links the Vulnerabilities and Infrastructure sheets when they are published. Provider remediation text is reduced to one clean line with absolute links; a record with no ask text renders its title (documents and policies as "Please provide: …") and Publish asks you to acknowledge the count.

## Inclusion rules

Deactivated and irrelevant records are omitted. With Include all off, only explicitly included records appear. Passing records appear only when Include OK is enabled. Policy records follow the policy exclusion setting. Infrastructure asks go to the Infrastructure report unless Include Infrastructure in Agenda is enabled. Asks are grouped under Overall, Infrastructure, Company Compliance and Employee Compliance with a deterministic precedence when flags overlap; the preview shows the effective grouping.

## Placeholders

Asks may use {children} and {count}. Children are listed as nested bullets, all of them by default (the child limit is "All"; a number keeps the first N and adds "and N others"); the count is taken after deduplication. Identical child labels are consolidated with multiplicity; people are deduplicated by stable identity rather than name. Vanta policy acceptance and awareness training combine into one Employee Compliance ask with each person listed once and the onboarding link when available.

## Status per framework

Each framework is a bullet with nested lines: Controls (passing of total, as a percentage), Evidence (tests and documents together, as in the template) and, for Drata, Monitors. Percentages round half to even and a partial result never rounds up to 100%. A missing denominator renders Unknown, never 0% or 100%.

## Safe updates

Preview shows the rendered sections and a semantic diff against the current document. Before writing, Agency fetches the document and its revision, recomputes the managed ranges and writes with revision control. If the document changed in the meantime, Agency refetches and shows a new preview instead of overwriting. Managed regions use named ranges when Agency created the document and the legacy VANTA_ASKS and VANTA_FRAMEWORKS marker pairs when importing an existing document. Missing, duplicated or nested markers stop the update and offer Repair or Save as New.

Creation, content, formatting and link insertion are separate recorded stages. A formatting failure yields "Created with formatting warnings" and a Repair action; it never creates a duplicate document. Unlinking locally never deletes the Google file.`,
  }),
  article({
    slug: 'infrastructure-sheet',
    title: 'Infrastructure report',
    section: 'workflows',
    order: 14,
    summary: 'Platform, Task, Status and Notes columns, stable-key merging and archived rows.',
    markdown: `# Infrastructure report

The Infrastructure report is a Google Sheet named "{Customer} Infrastructure" with the columns Platform, Task, Status and Notes and Comments, laid out like the Agency infrastructure task template: row 1 reads "Last updated September 1st, 2026 @ 9:32AM", row 2 is blank and the header sits on row 3. The Task cell is the same ask the agenda shows, followed by a blank line and every child finding on its own indented line (identical labels consolidated with multiplicity, computers with their owner's address).

## Human-owned columns

Platform, Task and the child lines are generated. Status and Notes and Comments belong to you and the customer. Status offers Pending Response and Completed. Rows are merged by a hidden stable source key, never by row position or task wording, so your values survive regeneration, sorting and changes in child count.

## Retired rows

A source row that no longer exists in the selected snapshot is moved to an archive area with a visible diff rather than silently deleted. Regenerating from an older snapshot never writes data back to the provider.

## Formatting

Agency uses native tables and filters where available and falls back to a range-based layout with a surfaced warning otherwise. Table availability never decides whether the data can be exported. Styling follows restrained Agency export defaults instead of the prototype's sheet colours.`,
  }),
  article({
    slug: 'employee-compliance-sheet',
    title: 'Employee Compliance report',
    section: 'workflows',
    order: 15,
    summary: 'Columns, employment states, check statuses and coverage notes for both providers.',
    markdown: `# Employee Compliance report

The Employee Compliance Sheet ("{Customer} Employee Compliance") is available for Vanta and for Drata when the connection's verified coverage includes personnel. It opens with the "Last updated …" banner (the outreach note next to it) and the header on row 3. Columns are Name, Email, Employment, Policies, Identity MFA, Background Check, Agent Installed, Password Manager, Disk Encryption, Antivirus, Auto Updates, Lock Screen, Security Training, HIPAA Training and AI Training. Employment reads Employee, Contractor or Future hire, as in the template sheet.

Employee compliance is handled per customer: as the Employee Compliance group of the rolling agenda, as this sheet, or as outreach mail. The account Overview names the three channels and keeps the customer's points of contact for the outreach Cc line.

## Employment states

Current employees, contractors and future hires are distinct rows. Former staff, out-of-scope identities and service accounts are excluded from the default report and shown as EXCLUDED when you include them. Outreach applies a stricter eligibility rule.

## Check statuses

In the app each check reports PASS, FAIL, DUE SOON, MISCONFIGURED, EXCLUDED, UNKNOWN or UNSUPPORTED. A provider field that is absent renders UNKNOWN; a dataset the connection cannot read renders UNSUPPORTED. In the sheet those two cells are left blank, like the template; neither is ever shown as a pass.

## Provenance

The sheet header records the account, snapshot date, generated-at time and coverage notes, and the source timestamp of the personnel dataset. Failed items sort first and can be filtered. The reusable outreach note explains how to act on the failures.`,
  }),
  article({
    slug: 'vulnerabilities',
    title: 'Vulnerabilities',
    section: 'workflows',
    order: 16,
    summary: 'Finding keys, package grouping, fixability handling and the four empty states.',
    markdown: `# Vulnerabilities

The Vulnerabilities view reads the selected local snapshot and offers an in-app report, CSV and JSON export and a linked Google Sheet. It never makes a provider request.

## Identity and grouping

Each finding is stored under a raw key made of provider, account or workspace, finding identifier, asset and package version. Grouping by package name is presentation only, so packages with the same name in different ecosystems never collapse into one row. The in-app summary groups actionable findings by package, shows the most severe representative, the earliest due date, the fixed version, the affected asset count and the finding count, and links to the detail rows. Sorting is by severity, then earliest due date, then package name with deterministic tie-breaking.

## The sheet

The linked sheet ("{Customer} Vulnerabilities") follows the Agency vuln sheet template. The first tab, Vulnerabilities, lists every finding with the columns Source, Asset, Asset id, Identifier, Package name, Fixed version, Severity, Due date and Fixable; the Asset cell links to the finding in the provider when a link is known, due dates are written as MM/DD/YYYY and Fixable shows a check mark or a cross. The second tab, Packages, has one row per package with the asset count and the finding count (the "findings" a package row refers to are its rows on the first tab). The Group Key and Finding Key columns that tie the tabs together are hidden. Both tabs open with the "Last updated …" banner and the header on row 3; the Info tab records the account, snapshot, generated-at time and coverage notes.

## Fixability

The actionable report excludes findings the provider marks non-fixable by default; they remain in the source explorer and behind a filter. Vanta fixability is taken from the provider when supplied. Drata monitor failures rarely state fixability, so Agency keeps them UNKNOWN and enables Include unknown fixability for Drata with a coverage note.

## Empty states

An empty report distinguishes No matching vulnerabilities, Dataset not synced, Access unavailable and Sync incomplete. No output claims there are no vulnerabilities because a request failed.`,
  }),
  article({
    slug: 'outreach-and-privacy',
    title: 'Outreach and privacy',
    section: 'workflows',
    order: 17,
    // real-providers change: drafts only; Agency never sends.
    summary: 'Task groups, eligibility, the To / Bcc / Cc shape, the exclusion of former and deactivated people, and Gmail drafts.',
    markdown: `# Outreach and privacy

Outreach prepares Gmail messages to customer staff who have outstanding compliance tasks. Agency creates drafts in your Gmail. Review and send each one from Gmail; Agency never sends.

## Task groups

Vanta task groups are Policies / Training (combined), Google Workspace MFA, Screenlock, Hard-disk Encryption, Anti-virus, Password Manager, background checks, device-monitoring installation, named custom tasks and applicable offboarding tasks. Each group shows category, status counts, task details, due dates, affected people and recipient selection. Drata task groups use verified personnel compliance checks; groups the connection cannot support are marked unsupported.

## Eligibility

A recipient is selected only when a current person, an active failing test entity and, for device tasks, a matched device owned by that person all agree. Deactivated entities, former staff, disabled tasks and completed tasks are excluded. If ten devices fail a local property but only five are active failing entities, the five matched owners are selected. Future hires and contractors require an explicit policy choice; ambiguous or missing identities are quarantined for review.

People who are being offboarded, whose record is deactivated or whose end date has passed never receive mail, whatever else is true: they are quarantined with the reason, the recipients section counts them as "Excluded", and before any draft is written every To, Cc and Bcc address is checked again against the snapshot's people. A customer contact address that matches a former or deactivated person is refused with a clear message, never dropped silently.

## Message shape

Group mode (recommended) follows the analyst's practice: the message goes To your own address, every employee is in Bcc, and the customer's point of contact is in Cc when they asked to be included. The contacts live on the account Overview (Employee compliance card) and are ticked in the compose pane; an employee address can never be a Cc. Personalized mode sends one message per recipient with the same Cc.

## Templates and privacy

Templates are the analyst's wording: "Hi, My name is {analyst_name} and I am a Security Analyst at Agency helping {company} with its compliance." followed by the task-specific paragraphs and the help links (hard-disk encryption, password manager, screen lock, Vanta Device Monitor, policies and training with {onboarding_url}). Variables are {client_name}, {company}, {task_name}, {employee_count}, {employee_list}, {analyst_name} (your first name from the connected Google identity, editable in the compose pane) and {onboarding_url}. Every draft carries a plain-text part and an HTML part, so the links are clickable in every mail client. The preview renders every variable and flags unresolved ones. In group mode recipients are always Bcc, never Cc, and the shared message does not contain {employee_list} because addresses in the body defeat Bcc privacy; templates that use it are restricted to internal summaries. All recipients are shown before drafting.

## Drafts

Create drafts writes one Gmail draft per message and records its id; the intent is persisted before any write. Each draft item offers Open in Gmail and the batch offers Open Drafts in Gmail, both for the connected Google identity shown next to the links. Recipient data older than 24 hours requires a manual sync before drafting; an exception records a reason with the batch, and Agency never runs that sync on its own.

Each item is prepared, draft created or failed. Repeating a task for the same recipients shows the prior batch time and warns about duplication.`,
  }),
  article({
    slug: 'rumi',
    title: 'Rumi',
    section: 'workflows',
    order: 18,
    summary: 'Credential assignment, Focused Answer and Full Review, the context manifest, citations and budgets.',
    markdown: `# Rumi

Rumi is a read-only analysis assistant over the active account's local snapshot. It uses the OpenAI Responses API with an Agency-owned key that an administrator assigns to you centrally. The setup screen shows whether an assignment is active; there is no field to enter, reveal or rotate a key. If no key is assigned, use Contact administrator.

## Modes

Focused Answer combines local full-text search, normalised relationships and deterministic filters to retrieve the evidence for a question. Full Review enumerates a selected dataset in bounded batches, tracks coverage and combines results with source references. Counts and dates are computed locally in code, not by the model.

## Context manifest

Before the first request and whenever the scope grows materially, Rumi shows the destination, account, selected snapshot, data categories, estimated context size and the configured AI policy. The manifest is kept locally and can be inspected later. Requests are sent with store set to false; conversations are kept on this Mac for 90 days unless you delete them.

## Answers

Every factual answer cites local record ids and the snapshot date and states coverage limits. Citations open the inspector. When evidence is missing, Rumi says "not available in this snapshot" rather than guessing. If cost, context size or a provider failure prevents full coverage, the answer is marked partial with the unprocessed count.

## Safety and cost

Provider descriptions, notes and retrieved text are untrusted content and cannot instruct Rumi to export data or call tools outside the account. Rumi cannot send email or publish documents; it can prepare a local draft that enters the normal review workflow. Each request shows a cost estimate, and a usage receipt follows completion. Cancelled or failed streamed requests may still incur charges, which are recorded as known or unknown usage. Full Review shows an estimate before it starts and is never rerun automatically after a crash.`,
  }),
  article({
    slug: 'notepad-and-lists',
    title: 'Notepad and personal lists',
    section: 'workflows',
    order: 19,
    summary: 'Write and Preview modes, outline, search, tasks, Priority and weekday lists.',
    markdown: `# Notepad and personal lists

The Notepad is a local Markdown scratchpad with Write and Preview modes and autosave. The outline follows your headings; search steps through matches with next and previous. Task checkboxes, headings, bullets, numbered lists, quotes, dividers, bold, italic, inline code and timestamp insertion are available from the toolbar and shortcuts. Preview is sanitised and never fetches remote resources.

A global scratchpad is always present. Account-specific notes use explicit account scope and stay on this Mac.

## Priority and weekday lists

Add accounts to Priority or Monday through Friday from the **Lists** menu on the All accounts page (open it from the account switcher's **Manage accounts**) to build personal work lists. The account switcher shows the Priority list and today's weekday list in order, each row with a done marker; done markers persist until you reset them on the All accounts page. A weekday list is a personal reminder, not a scheduled sync; Agency never collects data on a schedule.

## Account labels

Short labels, initials, pinning and ordering help distinguish accounts. The prototype's colour palette is preserved as a preference but rendered in Agency's monochrome hierarchy; colour is reserved for status.`,
  }),
  article({
    slug: 'source-field-mappings',
    title: 'Source field mappings',
    section: 'field_mappings',
    order: 30,
    summary: 'How Vanta and Drata fields map to normalised statuses, employment states and checks.',
    markdown: `# Source field mappings

Agency keeps the raw provider value next to every normalised value. The tables below describe the default mapping; the inspector always shows both.

## Standard status

| Provider value | Normalised |
| --- | --- |
| Vanta test OK, entity PASSING, document OK, policy APPROVED | PASS |
| Vanta test NEEDS_ATTENTION, entity FAILING, document NEEDS_ATTENTION, policy NEEDS_APPROVAL or EXPIRED | NEEDS ATTENTION |
| Vanta document DUE_SOON, tasks summary DUE_SOON | DUE SOON |
| Vanta DEACTIVATED, entity DEACTIVATED or IGNORED | DEACTIVATED |
| Vanta NOT_APPLICABLE or NOT_RELEVANT | NOT APPLICABLE |
| Vanta IN_PROGRESS or INVALID | UNKNOWN |
| Drata control isReady true | PASS |
| Drata control isReady false | FAIL |
| Drata control isReady null | UNKNOWN |
| Drata monitor PASSED | PASS |
| Drata monitor FAILED | FAIL |
| Drata monitor ERROR or PENDING | UNKNOWN |
| Drata monitor UNUSED, control archived or out of scope | DEACTIVATED |
| Dataset not readable by this connection | UNSUPPORTED |

## Employment

| Provider value | Agency state |
| --- | --- |
| Vanta CURRENT, Drata CURRENT_EMPLOYEE | current |
| Vanta CONTRACTOR, Drata CURRENT_CONTRACTOR | contractor |
| Vanta FUTURE_HIRE, Drata FUTURE_HIRE | future_hire |
| Vanta FORMER, Drata FORMER_EMPLOYEE or FORMER_CONTRACTOR | former |
| OUT_OF_SCOPE | out_of_scope |
| SERVICE_ACCOUNT | service_account |

## Employee checks

Vanta checks come from the person's tasks summary (policiesAccepted, securityTrainingCompleted, hipaaTrainingCompleted, aiTrainingCompleted, backgroundCheckCompleted, mfaEnabled) and from the owned monitored computer (agentInstalled, passwordManager, diskEncryption, antivirus, autoUpdates, screenLock). Drata checks come from complianceChecks with PASS, FAIL or UNKNOWN. A null or absent provider value is UNKNOWN. A person without a matched device shows device checks as UNKNOWN, and a dataset the connection cannot read shows UNSUPPORTED.

## Vulnerabilities

Vanta vulnerabilities map severity, package name, package identifier, fixed version, installed version, remediateByDate and isFixable directly. Drata monitor failures from vulnerability monitors map packageName, fixedVersion, severity and dueDate; fixability stays UNKNOWN unless the provider states it.`,
  }),
  article({
    slug: 'keyboard-shortcuts',
    title: 'Keyboard shortcuts',
    section: 'shortcuts',
    order: 40,
    summary: 'Global shortcuts, navigation, tables and the text editor.',
    markdown: `# Keyboard shortcuts

Every shortcut has a matching menu entry. Shortcuts that would edit text are suppressed while a text editor has focus. No destructive action or email send is bound to a shortcut without confirmation.

## Global

| Shortcut | Action |
| --- | --- |
| Command-K | Command palette: actions and account switching |
| Command-1 | Sources |
| Command-2 | Agendas |
| Command-3 | Vulnerabilities |
| Command-4 | Employee Compliance |
| Command-5 | Outreach |
| Command-B | Toggle the left rail |
| Command-J | Toggle the jobs panel |
| Command-F | Search in the current view |
| Command-, | Settings |
| Command-Shift-R | Toggle the Rumi panel |

## Tables

| Shortcut | Action |
| --- | --- |
| Up and Down arrows | Move the active row |
| Enter | Open the selected record in the inspector |
| Space | Toggle selection |
| Escape | Clear selection or close the inspector |

## Notepad

| Shortcut | Action |
| --- | --- |
| Command-E | Toggle Write and Preview |
| Command-Shift-T | Insert timestamp |
| Command-Shift-C | Toggle task checkbox |
| Command-G and Command-Shift-G | Next and previous search match |`,
  }),
  article({
    slug: 'retention-and-local-storage',
    title: 'Retention and local storage',
    section: 'storage_retention',
    order: 50,
    summary: 'Where data lives, the 30-day snapshot window, what persists and the storage budget.',
    markdown: `# Retention and local storage

Customer data lives in an encrypted SQLite database (SQLCipher) under the macOS Application Support directory for Agency, with one store per account and a separate profile database for application state. The renderer never reads these files; a single background process owns the database.

## Snapshot history

Every successfully committed snapshot from the last 30 days is kept with second-level timestamps. The latest complete snapshot is kept even when no sync has run for more than 30 days and is labelled stale. This is logical snapshot retention: a record version older than the window may remain when a retained snapshot needs it. Timeline entries exist only where a manual sync occurred.

Cleanup runs at launch, after each commit and during idle time, and covers staging areas, indexes, WAL files, diagnostics and migration backups. Incomplete staging data is kept for seven days. Agency warns at 80% of the configurable local storage budget and pauses before running out of disk rather than discarding in-window history.

## What persists

Account and connection identity, complete and partial history, source overrides, templates in use, unsent drafts, Google document and sheet mappings, infrastructure status and comments, Rumi conversations, notes, list order and done state, job checkpoints, the audit outbox, preferences and panel layout all survive upgrades. Reauthorisation reconnects to this state rather than creating a duplicate account.

## Different clocks

Settings lists each retention separately: source history 30 days; notes, overrides and artifact links until you delete them; Rumi conversations 90 days with an explicit delete; assembled-context caches 30 days; generated exports according to their destination. Encrypted local backups are explicit, exclude secrets and are never uploaded by Agency. Time Machine and other external backups follow your device policy.`,
  }),
  article({
    slug: 'error-reference',
    title: 'Error reference',
    section: 'errors',
    order: 60,
    summary: 'Every stable error code, what it means and the suggested remedy.',
    markdown: `# Error reference

Every sync issue carries a stable error code. The Jobs panel shows the code with a suggested remedy; this page explains each one. Raw provider responses stay encrypted on this Mac and are never included in Agency telemetry.

## Connectivity

- **network**: the request could not reach the provider. Check connectivity and Resume; the job keeps its checkpoint.
- **timeout**: the provider did not answer within 60 seconds. Resume; repeated timeouts on one dataset suggest a provider incident.
- **rate_limited**: the provider returned 429. Agency waits for Retry-After or backs off with jitter and shows the countdown. Nothing is lost.
- **server_error**: the provider returned a 5xx. Retried automatically within the budget, then paused.

## Credentials and permissions

- **unauthorized**: the token or API key was rejected. Reconnect the provider. Repeated 401s after one renewal are reported instead of looping.
- **token_conflict**: another client minted a token for the same Vanta application. Coordinate with the other user or use a distinct credential, then Reconnect.
- **forbidden**: the provider refused the request. Ask the customer to grant the missing permission; Agency does not retry persistent 403s.
- **permission_missing**: a dataset needs a scope this credential lacks. The dataset is marked unsupported until the scope is granted.
- **terms_not_accepted**: Drata requires terms acceptance in its console before the API can be used.
- **tenant_mismatch**: the credential belongs to a different tenant than this account. Select or create the correct account.
- **workspace_ambiguous**: several Drata workspaces are reachable. Choose one in the workspace picker.

## Data integrity

- **malformed_page**: a page did not match the expected shape. The page is kept for diagnosis; contact an administrator.
- **duplicate_cursor**: the provider returned the same cursor twice. Agency stops the dataset rather than looping; Resume restarts it.
- **duplicate_id**: the same record id appeared twice in one traversal. Resume restarts the dataset.
- **missing_continuation**: a page indicated more data without a cursor. Resume restarts the dataset.
- **unexpected_empty_page**: an empty page arrived before the traversal ended. Resume restarts the dataset.
- **total_mismatch**: the provider reported a different total than was fetched; counts alone cannot prove completeness.
- **not_found**: a referenced parent no longer exists. The child dataset is skipped for that parent and reported.
- **unsupported_dataset**: the adapter does not support this dataset for this provider or plan.

## Local conditions

- **disk_full**: free disk fell below the safety margin. Free space and Resume; no history was discarded.
- **lease_unavailable**: the coordination lease could not be acquired. Wait and Resume.
- **entitlement_expired**: your entitlement expired mid-run. Sign in and Resume.
- **cancelled**: you cancelled the job. The safe checkpoint was kept.
- **internal**: an unexpected local error. Export a support bundle and contact an administrator with the run id.`,
  }),
  article({
    slug: 'release-notes',
    title: 'Release notes',
    section: 'release_notes',
    order: 70,
    summary: 'What changed in each Agency release and content bundle.',
    markdown: `# Release notes

## Agency 1.0.0-local

First local-mode release for usability testing. Every product behaviour runs as real code; only the external services (identity provider, Vanta, Drata, Google, OpenAI, mail) are simulated at the HTTP boundary.

- Accounts: registration, renaming, notes, assignment, connection replacement, archive and local deletion with stable account ids.
- Sync: manual jobs with preflight scope preview, per-page checkpoints, rate limiting, Retry Failed Parts, Resume and Cancel; partial runs kept separately from complete snapshots.
- Sources: dense table with inspector, search, views, independent filters, overrides that survive sync and JSON export with manifest.
- Agendas: the Agency rolling agenda layout with tabs, inclusion rules, {children} and {count}, framework status with numerators and denominators, revision-aware Google Docs updates.
- Reports: Infrastructure, Employee Compliance and Vulnerabilities sheets in the template layouts with stable-key merging, unknown fixability handling and the four empty states.
- Outreach: task groups, eligibility intersection, To self / Bcc employees / Cc the customer contact, the analyst's templates with clickable links, Gmail drafts only.
- Rumi: Focused Answer and Full Review with local counting, context manifest, citations, budgets and usage receipts.
- Administration: people and access, accounts and devices, API key assignment, template lifecycle with preview against synthetic fixtures, usage, audit and system operations.

## Content bundle 1.1.0

Template feedback (2026-09-25): the agenda template is the Agency rolling agenda (bold header block, "Monday, August 10th, 2026 | Asynchronous Update", emoji headings, "- N/A" operator sections, every ask as one bullet with nested children, italic group labels, Controls and Evidence per framework, the sprint history on its own tab and the Overall cap with an Additional asks tab); remediation asks are written in the customer's words; sheets follow the vulnerability and infrastructure templates (banner rows, the template columns, MM/DD/YYYY due dates, check-mark and cross fixability, the rendered ask as the task); the email templates are the analyst's five bodies with {analyst_name} and {onboarding_url}, drafted To yourself with employees in Bcc and the customer contact in Cc, with an HTML part for clickable links, and former, offboarding or deactivated people never receive mail. Existing desktops adopt the bundle through Content refresh; agendas keep their pinned template version until Adopt for all agendas.

## Content bundle 1.0.0

Initial default bundle: agenda template, email templates for every task group, generic remediation asks for common Vanta tests and Drata controls, classification rules, provider matching rules, report column definitions, this documentation, model configuration, rate-limit configuration and feature flags.`,
  }),
  article({
    slug: 'support-references',
    title: 'Support references',
    section: 'support',
    order: 80,
    summary: 'What to send when asking for help, and what never to send.',
    markdown: `# Support references

Support starts with a run id and a sanitised support bundle, never with a copy of a customer database.

## Finding the run id

Open Sync History or the Jobs panel, select the run and use Copy run id. The run id is a UUID and is safe to share. Each issue row also offers Copy reference, which copies the run id, dataset, endpoint template, page ordinal and stable error code as one line.

## Support bundle

Settings offers Export support bundle. The bundle is previewable before export and contains app and OS versions, the device id, recent job transitions, scrubbed issue summaries, API call metadata (endpoint templates, status codes, durations, WAIT events and byte counts) and local diagnostics. It never contains provider responses, record bodies, employee addresses, tokens, API keys or notes. Review the preview, then attach the exported file.

## What to include

- The run id or copied issue reference.
- The account display name (not its data).
- What you expected and what you saw, with timestamps in UTC.
- The support bundle, if the problem involves a sync, a publish or Rumi.

## Never include

- Screenshots of customer records, employee lists or Rumi answers.
- Exported JSON, CSV or sheet contents.
- Credentials, tokens or the contents of the Keychain.

## Contact

Use the internal support channel or email support@getagency.com. Administrators can see audit delivery status and device health in the admin console without access to your local data.`,
  }),
];

export const ERROR_HELP: Record<SyncErrorCode, { title: string; summary: string; remedy: string; steps: string[] }> = {
  network: { title: 'Network unreachable', summary: 'The request could not reach the provider.', remedy: 'Check connectivity, then Resume. The checkpoint is intact.', steps: ['Confirm this Mac is online.', 'Open the job and choose Resume.', 'If it recurs, check the provider status page.'] },
  timeout: { title: 'Provider timeout', summary: 'The provider did not answer within the response timeout.', remedy: 'Resume. Repeated timeouts on one dataset indicate a provider incident.', steps: ['Choose Resume on the paused job.', 'If the same page times out three times, pause and retry later.'] },
  rate_limited: { title: 'Rate limited', summary: 'The provider returned 429. Agency is waiting for Retry-After or backing off with jitter.', remedy: 'Wait. The countdown is shown on the job; no data is lost.', steps: ['Let the countdown finish.', 'If the wait exceeds the retry budget, the job pauses; Resume later.'] },
  unauthorized: { title: 'Credentials rejected', summary: 'The token or API key was rejected after one renewal attempt.', remedy: 'Reconnect the provider with valid credentials.', steps: ['Open the account and choose Reconnect.', 'Enter the current credentials and run Connect and Test.', 'Resume the job.'] },
  token_conflict: { title: 'Token conflict', summary: 'Another client minted a token for the same Vanta application.', remedy: 'Coordinate with the other user or use a distinct credential, then Reconnect.', steps: ['Check whether a colleague or script uses the same Vanta application.', 'Prefer one credential per analyst.', 'Reconnect and Resume.'] },
  forbidden: { title: 'Forbidden', summary: 'The provider refused the request for this credential.', remedy: 'Ask the customer to grant the missing permission. Agency does not retry persistent 403s.', steps: ['Note the endpoint template shown on the issue.', 'Ask the customer administrator to grant the corresponding scope.', 'Run Connect and Test to refresh the capability manifest.'] },
  not_found: { title: 'Parent not found', summary: 'A referenced parent record no longer exists in the provider.', remedy: 'No action. The child dataset is skipped for that parent and reported.', steps: ['Review the reported parent id in the issue.', 'Run Sync Now to collect the current state.'] },
  malformed_page: { title: 'Malformed page', summary: 'A response did not match the expected shape.', remedy: 'Contact an administrator with the run id. The page is kept encrypted for diagnosis.', steps: ['Copy the issue reference.', 'Export a support bundle.', 'Contact an administrator.'] },
  duplicate_cursor: { title: 'Repeated cursor', summary: 'The provider returned the same pagination cursor twice.', remedy: 'Resume restarts the affected dataset from its last valid checkpoint.', steps: ['Choose Resume.', 'If it recurs on the same dataset, report it with the run id.'] },
  duplicate_id: { title: 'Duplicate record id', summary: 'The same record id appeared twice in one traversal.', remedy: 'Resume restarts the affected dataset.', steps: ['Choose Resume.', 'If it recurs, report it with the run id.'] },
  missing_continuation: { title: 'Missing continuation', summary: 'A page indicated more data but carried no cursor.', remedy: 'Resume restarts the affected dataset.', steps: ['Choose Resume.'] },
  total_mismatch: { title: 'Provider total mismatch', summary: 'The provider reported a different record total than the traversal fetched.', remedy: 'Counts alone cannot prove completeness; review the dataset or re-run the sync.', steps: ['Compare the dataset count with the provider console.', 'Run Sync Now again if the difference persists.'] },
  unexpected_empty_page: { title: 'Unexpected empty page', summary: 'An empty page arrived before the traversal ended.', remedy: 'Resume restarts the affected dataset.', steps: ['Choose Resume.'] },
  server_error: { title: 'Provider error', summary: 'The provider returned a 5xx response.', remedy: 'Retried automatically within the budget, then paused. Resume later.', steps: ['Wait a few minutes.', 'Choose Resume.'] },
  unsupported_dataset: { title: 'Dataset unsupported', summary: 'The adapter does not support this dataset for this provider or plan.', remedy: 'No action. Reports show the dataset as unsupported.', steps: ['Review the capability manifest under the connection.'] },
  permission_missing: { title: 'Permission missing', summary: 'The credential lacks a scope this dataset requires.', remedy: 'Ask the customer to grant the scope, then run Connect and Test.', steps: ['Note the dataset on the issue.', 'Request the scope from the customer administrator.', 'Run Connect and Test.'] },
  workspace_ambiguous: { title: 'Workspace ambiguous', summary: 'Several Drata workspaces are reachable with this key.', remedy: 'Choose a workspace in the picker and reconnect.', steps: ['Open Reconnect.', 'Select the intended workspace.', 'Run Connect and Test.'] },
  tenant_mismatch: { title: 'Tenant mismatch', summary: 'The credential belongs to a different tenant than this account.', remedy: 'Select or create the correct account before attaching data.', steps: ['Compare the discovered tenant with the account.', 'Create a new account or choose the matching one.'] },
  terms_not_accepted: { title: 'Terms not accepted', summary: 'Drata requires API terms acceptance in its console.', remedy: 'Ask the customer administrator to accept the API terms, then retry.', steps: ['Ask the customer to accept the terms in Drata.', 'Run Connect and Test.'] },
  disk_full: { title: 'Disk space low', summary: 'Free disk fell below the safety margin.', remedy: 'Free space, then Resume. No history was discarded.', steps: ['Free at least the amount shown in Settings.', 'Choose Resume.'] },
  lease_unavailable: { title: 'Coordination lease unavailable', summary: 'The shared rate-limit lease could not be acquired.', remedy: 'Wait and Resume.', steps: ['Wait for the lease holder to finish.', 'Choose Resume.'] },
  entitlement_expired: { title: 'Entitlement expired', summary: 'Your one-hour entitlement expired during the run.', remedy: 'Sign in, then Resume.', steps: ['Sign in when prompted.', 'Choose Resume.'] },
  cancelled: { title: 'Cancelled', summary: 'You cancelled the job. The last safe checkpoint was kept.', remedy: 'Resume when ready.', steps: ['Choose Resume or start a new Sync Now.'] },
  internal: { title: 'Internal error', summary: 'An unexpected local error occurred.', remedy: 'Export a support bundle and contact an administrator with the run id.', steps: ['Copy the run id.', 'Export a support bundle.', 'Contact an administrator.'] },
};

/** Slug of the per-code error article, e.g. `error-rate-limited`. */
export function errorHelpSlug(code: SyncErrorCode): string {
  return `error-${code.replace(/_/g, '-')}`;
}

/** One article per stable error code so the Jobs panel can deep-link by code. */
export const ERROR_HELP_ARTICLES: readonly HelpArticle[] = SYNC_ERROR_CODES.map((code, i) => {
  const entry = ERROR_HELP[code];
  return article({
    slug: errorHelpSlug(code),
    title: `${entry.title} (${code})`,
    section: 'errors',
    order: 61 + i,
    summary: entry.summary,
    markdown: [
      `# ${entry.title}`,
      '',
      `Stable error code: \`${code}\``,
      '',
      entry.summary,
      '',
      '## Suggested remedy',
      '',
      entry.remedy,
      '',
      '## Steps',
      '',
      ...entry.steps.map((s, n) => `${n + 1}. ${s}`),
      '',
      'The issue row in the Jobs panel shows the run id, dataset, endpoint template, page ordinal, HTTP status, provider request id, retry attempt and next retry for this error. Raw provider responses stay encrypted on this Mac and are never sent to Agency.',
    ].join('\n'),
  });
});

/** All articles shipped in the bundle, in display order. */
export const ALL_HELP_ARTICLES: readonly HelpArticle[] = [...HELP_ARTICLES, ...ERROR_HELP_ARTICLES].slice().sort((a, b) => a.order - b.order);

export const SUPPORT_REFERENCES = {
  supportEmail: 'support@getagency.com',
  whatToInclude: ['Run id or copied issue reference', 'Account display name', 'Expected and observed behaviour with UTC timestamps', 'Sanitised support bundle when a sync, publish or Rumi request is involved'],
  neverInclude: ['Screenshots of customer records, employee lists or Rumi answers', 'Exported JSON, CSV or sheet contents', 'Credentials, tokens or Keychain contents'],
} as const;

/** Sanity: every stable error code has help text. */
export const ERROR_HELP_COVERS_ALL_CODES = SYNC_ERROR_CODES.every((code) => code in ERROR_HELP);
